import { requestGptJson } from './bid-ai-api';

export interface ExtractedEstimateData {
  vendor_name: string;
  estimate_total: string;
  line_items: string[];
}

const buildPrompt = (rawText: string) => `You are an assistant that extracts structured data from construction estimates.

Input: raw, unstructured text from a parsed PDF estimate.

Task:
- Extract the vendor or company name that issued the estimate.
- Extract the total value of the estimate, exactly as shown (e.g., "$45,800.00").
- Extract the ENTIRE list of line items in the estimate. A line item is any specific task, material, or service being estimated (e.g., "Concrete foundation 3000 PSI – 50 CY"). Ignore headers, footers, page numbers, subtotals, taxes, notes, and irrelevant text. Return every line item exactly as it appears.

Respond with ONLY a valid JSON object in exactly this format:

{
  "vendor_name": "Company Name Here",
  "estimate_total": "$45,800.00",
  "line_items": ["Concrete foundation 3000 PSI", "Steel reinforcement #4 rebar"]
}

Raw PDF text:
${rawText}`;

/** Reads a bid PDF's text into what a Comparison Sheet column needs: the vendor, its total and its line items. */
export async function extractEstimateData(rawText: string): Promise<ExtractedEstimateData> {
  let data: any;
  try {
    data = JSON.parse(await requestGptJson(buildPrompt(rawText)));
  } catch (error) {
    if (error instanceof SyntaxError) throw new Error('The AI could not read this PDF. Please try again.');
    throw error;
  }
  if (!Array.isArray(data?.line_items)) throw new Error('The AI could not find line items in this PDF.');
  return {
    vendor_name: typeof data.vendor_name === 'string' ? data.vendor_name.trim() : '',
    estimate_total: typeof data.estimate_total === 'string' ? data.estimate_total.trim() : '',
    line_items: data.line_items.filter((item: unknown): item is string => typeof item === 'string' && item.trim() !== ''),
  };
}
